import React, { useState } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
} from "@mui/material";
import {
  Search,
  Refresh,
  Download,
  History,
  CheckCircle,
  Cancel,
  Edit,
  Delete,
  Add,
  Login,
  Logout,
  Visibility,
  Settings,
  Person,
  Business,
  Security,
} from "@mui/icons-material";
import {
  ADMIN_ROLES,
  ROLE_LABELS,
  ROLE_COLORS,
  PERMISSIONS,
  userCan,
} from "../../../config/adminRoles";

// Action type icons
const actionIcons = {
  login: <Login sx={{ fontSize: 18 }} />,
  logout: <Logout sx={{ fontSize: 18 }} />,
  create: <Add sx={{ fontSize: 18 }} />,
  update: <Edit sx={{ fontSize: 18 }} />,
  delete: <Delete sx={{ fontSize: 18 }} />,
  approve: <CheckCircle sx={{ fontSize: 18 }} />,
  reject: <Cancel sx={{ fontSize: 18 }} />,
  view: <Visibility sx={{ fontSize: 18 }} />,
  settings: <Settings sx={{ fontSize: 18 }} />,
};

// Action type colors
const actionColors = {
  login: "#22c55e",
  logout: "#64748b",
  create: "#6366f1",
  update: "#f59e0b",
  delete: "#ef4444",
  approve: "#22c55e",
  reject: "#ef4444",
  view: "#3b82f6",
  settings: "#8b5cf6",
};

const ActivityLogs = () => {
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const currentUser = validateUser?.UserValidate?.data?.user;

  const [searchQuery, setSearchQuery] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Placeholder activity logs - Backend will provide this
  const [activityLogs] = useState([
    {
      _id: "1",
      admin: { name: "Super Admin", email: "superadmin@ots.com", role: ADMIN_ROLES.SUPER_ADMIN },
      action: "login",
      description: "Admin logged in successfully",
      target: null,
      ip: "192.168.1.1",
      userAgent: "Chrome/120.0 Windows",
      createdAt: new Date().toISOString(),
    },
    {
      _id: "2",
      admin: { name: "Super Admin", email: "superadmin@ots.com", role: ADMIN_ROLES.SUPER_ADMIN },
      action: "approve",
      description: "Approved landlord registration",
      target: { type: "landlord", name: "John Doe", id: "land123" },
      ip: "192.168.1.1",
      userAgent: "Chrome/120.0 Windows",
      createdAt: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      _id: "3",
      admin: { name: "Super Admin", email: "superadmin@ots.com", role: ADMIN_ROLES.SUPER_ADMIN },
      action: "approve",
      description: "Approved property listing",
      target: { type: "property", name: "Flat 101", id: "prop456" },
      ip: "192.168.1.1",
      userAgent: "Chrome/120.0 Windows",
      createdAt: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      _id: "4",
      admin: { name: "Super Admin", email: "superadmin@ots.com", role: ADMIN_ROLES.SUPER_ADMIN },
      action: "create",
      description: "Created new admin account",
      target: { type: "admin", name: "New Admin", id: "admin789" },
      ip: "192.168.1.1",
      userAgent: "Chrome/120.0 Windows",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      _id: "5",
      admin: { name: "Super Admin", email: "superadmin@ots.com", role: ADMIN_ROLES.SUPER_ADMIN },
      action: "reject",
      description: "Rejected landlord registration",
      target: { type: "landlord", name: "Jane Smith", id: "land456" },
      ip: "192.168.1.1",
      userAgent: "Chrome/120.0 Windows",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
    },
    {
      _id: "6",
      admin: { name: "Super Admin", email: "superadmin@ots.com", role: ADMIN_ROLES.SUPER_ADMIN },
      action: "settings",
      description: "Updated platform settings",
      target: { type: "settings", name: "Platform Fee", id: "set001" },
      ip: "192.168.1.1",
      userAgent: "Chrome/120.0 Windows",
      createdAt: new Date(Date.now() - 259200000).toISOString(),
    },
  ]);

  // Filter logs
  const filteredLogs = activityLogs.filter((log) => {
    const matchesSearch =
      (log.admin.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (log.description?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (log.target?.name?.toLowerCase() || "").includes(searchQuery.toLowerCase());

    const matchesAction = filterAction === "all" || log.action === filterAction;

    return matchesSearch && matchesAction;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    // Less than 1 minute
    if (diff < 60000) return "Just now";
    // Less than 1 hour
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    // Less than 24 hours
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    // Less than 7 days
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;

    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  // Check permission
  if (!userCan(currentUser, PERMISSIONS.VIEW_ACTIVITY_LOGS)) {
    return (
      <>
        <AdminWrapper />
        <Box
          sx={{
            ml: { xs: 0, md: open ? "260px" : "65px" },
            transition: "margin 0.3s ease",
            minHeight: "100vh",
            bgcolor: "#f8fafc",
            p: { xs: 2, md: 3 },
            pt: { xs: 10, md: 11 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper sx={{ p: 4, textAlign: "center", maxWidth: 400 }}>
            <Security sx={{ fontSize: 60, color: "#ef4444", mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Access Denied
            </Typography>
            <Typography color="text.secondary">
              You don't have permission to view activity logs.
            </Typography>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Activity Logs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track all admin actions and system events
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              Export Logs
            </Button>
          </Box>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#6366f1" }}>
                {activityLogs.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Activities
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#22c55e" }}>
                {activityLogs.filter((l) => l.action === "approve").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Approvals
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#ef4444" }}>
                {activityLogs.filter((l) => l.action === "reject").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rejections
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, borderRadius: 2, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#f59e0b" }}>
                {activityLogs.filter((l) => l.action === "login").length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Logins
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by admin, action, or target..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={filterAction}
                  label="Action Type"
                  onChange={(e) => setFilterAction(e.target.value)}
                >
                  <MenuItem value="all">All Actions</MenuItem>
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="logout">Logout</MenuItem>
                  <MenuItem value="create">Create</MenuItem>
                  <MenuItem value="update">Update</MenuItem>
                  <MenuItem value="delete">Delete</MenuItem>
                  <MenuItem value="approve">Approve</MenuItem>
                  <MenuItem value="reject">Reject</MenuItem>
                  <MenuItem value="settings">Settings</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery("");
                  setFilterAction("all");
                }}
                sx={{ borderColor: "#e5e7eb" }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Activity Logs Table */}
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Admin</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((log) => (
                    <TableRow key={log._id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: `${ROLE_COLORS[log.admin.role]}20`,
                              color: ROLE_COLORS[log.admin.role],
                              fontSize: 14,
                            }}
                          >
                            {log.admin.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {log.admin.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ROLE_LABELS[log.admin.role]}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={actionIcons[log.action]}
                          label={log.action.charAt(0).toUpperCase() + log.action.slice(1)}
                          size="small"
                          sx={{
                            bgcolor: `${actionColors[log.action]}15`,
                            color: actionColors[log.action],
                            fontWeight: 500,
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.description}</Typography>
                      </TableCell>
                      <TableCell>
                        {log.target ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {log.target.type === "landlord" && <Person sx={{ fontSize: 16, color: "#22c55e" }} />}
                            {log.target.type === "property" && <Business sx={{ fontSize: 16, color: "#e94560" }} />}
                            {log.target.type === "admin" && <Security sx={{ fontSize: 16, color: "#6366f1" }} />}
                            <Typography variant="body2">{log.target.name}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {log.ip}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={new Date(log.createdAt).toLocaleString()}>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(log.createdAt)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                {filteredLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <History sx={{ fontSize: 50, color: "#e5e7eb", mb: 1 }} />
                      <Typography color="text.secondary">No activity logs found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredLogs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </Box>
    </>
  );
};

export default ActivityLogs;
